/**
 * Factory for creating GetPriceAndStockInformation requests.
 */
import {ICase, IFlatRateUnit, IJob, IPackage, IPart, ISublet, IUser} from '@service-and-repairs/awpintegrationlib';
import {GetPriceAndStockInformationRequest} from "./get-price-and-stock-information-request";
import {AwpDmsInterfaceFactoryUtils} from "../../rest-api/awp-dms-interface-factory-utils";
import {GetPriceAndStockInformationRequestBody} from "./get-price-and-stock-information-request-body";
import {CostSplit} from "../common/cost-split";
import {DmsFlatRateUnit} from "../common/dms-flat-rate-unit";
import {ServicePackage} from "../common/service-package";
import {DmsPart} from "../common/dms-part";
import {DmsSublet} from "../common/dms-sublet";

export class AwpDmsInterfacePriceStockRequestFactory {

  createRequest(user: IUser, serviceCase: ICase): GetPriceAndStockInformationRequest {
    const req = new GetPriceAndStockInformationRequest();
    req.functionalHeader = AwpDmsInterfaceFactoryUtils.createFunctionalHeader(serviceCase);
    req.technicalHeader = AwpDmsInterfaceFactoryUtils.createTechnicalHeaderInRequest(
      'awp-dms-get-price-and-stock-information-request',
      '1.3.0'
    );
    req.userInformation = AwpDmsInterfaceFactoryUtils.createUserInformation(user);
    req.body = this.createRequestBody(serviceCase);
    return req;
  }

  createRequestBody(serviceCase: ICase): GetPriceAndStockInformationRequestBody {
    const body = new GetPriceAndStockInformationRequestBody();
    body.brand = AwpDmsInterfaceFactoryUtils.createBrandString(serviceCase.getBrand());
    body.dmsCustomerId = serviceCase.getDmsCustomerNumber()?.trim() || undefined;
    body.dmsServiceOrderId = serviceCase.getDmsOrderNumber();

    // Request prices for both order and consultation list
    const allJobs: IJob[] = serviceCase.getJobs().concat(serviceCase.getConsultationJobs());
    body.flatRateUnitList = [];
    body.packageList = [];
    body.partList = [];
    body.subletList = [];

    for (const job of allJobs) {
      const costSplits = AwpDmsInterfaceFactoryUtils.getCostSplitsForJob(job);
      // Jobs either contain packages or parts/sublets/FRUs
      if (job.getPackages()?.length > 0) {
        body.packageList.push(...this.createPackageList(job.getPackages(), costSplits));
      } else {
        body.flatRateUnitList.push(...this.createFlatRateUnitList(job.getFlatRateUnits(), costSplits));
        body.partList.push(...this.createPartList(job.getParts(), costSplits));
        body.subletList.push(...this.createSubletList(job.getSublets(), costSplits));
      }
    }

    body.vin17 = serviceCase.getVinLong();
    body.returnOnlyPrices = false;
    return body;
  }

  createPackageList(packages: IPackage[], costSplitList: CostSplit[]): ServicePackage[] {
    return packages.map(p => ({
      campaign: p.getCampaignNumber() ? {
        campaignName: p.getCampaignName(),
        campaignNumber: p.getCampaignNumber(),
        campaignPriceType: 'LumpSum' // TODO: Clarify values and origin
      } : null,
      costSplitList: costSplitList,
      description: p.getDescription(),
      flatRateUnitList: this.createFlatRateUnitList(p.getFlatRateUnits(), costSplitList),
      isBindingLumpSum: p.getType() === 'GRP' || p.getType() === 'SIP' || p.getType() === 'MKP', // TODO: Clarify if this is correct
      isLocal: p.getIsLocal(),
      number: p.getNumber(),
      offerPrice: null,
      packageType: p.getType(),
      partList: this.createPartList(p.getParts(), costSplitList),
      subletList: this.createSubletList(p.getSublets(), costSplitList)
    }));
  }

  createFlatRateUnitList(flatRateUnits: IFlatRateUnit[], costSplitList: CostSplit[] = []): DmsFlatRateUnit[] {
    return flatRateUnits.map(fru => ({
      costSplitList: costSplitList,
      description: fru.getDescription(),
      flatRateUnitType: fru.getType(),
      isLocal: fru.getIsLocal(),
      number: fru.getNumber(),
      offerPrice: null,
      quantity: fru.getAmount()
    }));
  }

  createPartList(parts: IPart[], costSplitList: CostSplit[] = []): DmsPart[] {
    return parts.map(part => ({
      costSplitList: costSplitList,
      description: part.getDescription(),
      isLocal: part.getIsLocal(),
      number: part.getPartNumber(),
      offerPrice: null,
      quantity: part.getQuantity(),
      unit: 'pcs' // TODO: Mandatory field in the backend, but we don't have it in the basket
    }));
  }

  createSubletList(sublets: ISublet[], costSplitList: CostSplit[] = []): DmsSublet[] {
    return sublets.map((sublet: ISublet): DmsSublet => ({
      costSplitList: costSplitList,
      description: sublet.getDescription(),
      guid: sublet.getExternalId(),
      offerPrice: null,
      quantity: sublet.getQuantity(),
      subletCode: sublet.getSubletCode()
    }));
  }
}
